import { useState } from "react";
import Toast from "src/components/toast"; // Uncomment in your actual project
import sendEmail from "src/services/send-email"; // Uncomment in your actual project

function ContactUs() {
    const ERROR_TYPES = {
        NONE: '',
        REQUIRED: 'This field is required.',
        INVALID_EMAIL: 'Invalid email format.',
        INVALID_PHONE: 'Invalid phone number format.',
    };

    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '', message: '', honeyPot: '' });
    const [toast, setToast] = useState({ message: '', type: '' });
    const [errors, setErrors] = useState({
        firstName: ERROR_TYPES.NONE,
        lastName: ERROR_TYPES.NONE,
        email: ERROR_TYPES.NONE,
        phoneNumber: ERROR_TYPES.NONE,
        message: ERROR_TYPES.NONE,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });

        if (errors[name] !== ERROR_TYPES.NONE) {
            setErrors({ ...errors, [name]: validateField(name, value) });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setErrors({ ...errors, [name]: validateField(name, value) });
    };

    const validateField = (name, value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,8}$/;

        switch (name) {
            case 'firstName':
            case 'lastName':
            case 'message':
                return value.trim() === '' ? ERROR_TYPES.REQUIRED : ERROR_TYPES.NONE;

            case 'email':
                if (value.trim() === '') return ERROR_TYPES.REQUIRED;
                if (!emailRegex.test(value)) return ERROR_TYPES.INVALID_EMAIL;
                return ERROR_TYPES.NONE;

            case 'phoneNumber':
                if (value.trim() === '') return ERROR_TYPES.REQUIRED;
                // FIXED BUG: removed the semicolon that was breaking the if statement
                if (!phoneRegex.test(value)) return ERROR_TYPES.INVALID_PHONE;
                return ERROR_TYPES.NONE;

            default:
                return ERROR_TYPES.NONE;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formErrors = {
            firstName: validateField('firstName', form.firstName),
            lastName: validateField('lastName', form.lastName),
            email: validateField('email', form.email),
            phoneNumber: validateField('phoneNumber', form.phoneNumber),
            message: validateField('message', form.message),
        };

        setErrors(formErrors);

        const hasErrors = Object.values(formErrors).some((error) => error !== ERROR_TYPES.NONE);

        if (hasErrors) {
            return;
        }
        setIsSubmitting(true);

        const submitForm = {
            ...form,
            name: form.firstName + " " + form.lastName
        };
        
        // Mocked for example purposes. Use your actual sendEmail function.
        const result = await sendEmail(submitForm);
        // const result = { success: true }; // Placeholder

        if (result.success) {
            setToast({ message: 'Email sent successfully!', type: 'success' });
            setForm({ firstName: '', lastName: '', email: '', phoneNumber: '', message: '', honeyPot: '' });
        } else {
            setToast({ message: result.error || 'Failed to send', type: 'error' });
        }

        setIsSubmitting(false);
    };

    return (
        <div className="pt-[100px] md:pt-[139px]">
            {/* Main Wrapper: flex-col on mobile, flex-row on desktop. Adjusted padding for mobile/tablet */}
            <div className="flex flex-col lg:flex-row px-6 md:px-16 xl:px-[240px] py-12 lg:py-[112px] gap-12 lg:gap-[56px]">
                
                {/* Left Column (Text & Contact Info) */}
                <div className="w-full lg:w-1/2 flex flex-col gap-8 lg:gap-[32px]">
                    <div>
                        <h1 className="text-[32px] lg:text-[48px] font-semibold text-left leading-tight">We’d Love to Hear From You.</h1>
                        <p className="text-[16px] lg:text-[20px] text-left text-grey mt-4">Got a question about what we do, need some assistance, or have a project in mind? Drop us a line. Our team is always here to help you find the right solution.</p>
                    </div>
                    <div className="text-left flex flex-col gap-6 lg:gap-[16px]">
                        <div className="flex gap-4 lg:gap-[16px] items-start">
                            <img className="w-[48px] lg:w-[60px] shrink-0" src="/icons/email-yellow.svg" alt="Email icon" />
                            <div>
                                <p className="text-grey text-[14px] lg:text-base">Email:</p>
                                <p className="text-[16px] lg:text-[20px] font-semibold break-all">contact@harmonimitra.co.id</p>
                            </div>
                        </div>
                        <div className="flex gap-4 lg:gap-[16px] items-start">
                            <img className="w-[48px] lg:w-[60px] shrink-0" src="/icons/location-yellow.svg" alt="Location icon" />
                            <div>
                                <p className="text-grey text-[14px] lg:text-base">Location:</p>
                                <p className="text-[16px] lg:text-[20px] font-semibold leading-relaxed">Menara Cakrawala, Jl. M.H. Thamrin Jl. MH Thamrin No.Kav. 9 12th Floor Unit 05A, RT.2/RW.1, Kb. Sirih, Kec. Menteng, Kota Jakarta Pusat, Daerah Khusus Ibu Kota Jakarta</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column (Form) */}
                <div className="w-full lg:w-1/2 flex justify-center items-center">
                    <form onSubmit={handleSubmit} className="flex flex-col justify-center items-center bg-gray-50 w-full py-8 lg:py-[32px] px-6 lg:px-[24px] text-left gap-4 lg:gap-[16px] rounded-[8px] shadow-sm border border-gray-100">
                        
                        {/* Name Fields: Stacks on mobile, side-by-side on sm screens and up */}
                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[8px] w-full">
                            <div className="flex flex-col w-full">
                                <label className="text-[14px] lg:text-base font-medium mb-1">First Name<span className="text-red-600">*</span></label>
                                <input onBlur={handleBlur} value={form.firstName} onChange={handleChange} id="firstName" name="firstName" type="text" placeholder="First Name" className="border-[1px] p-[10px] lg:p-[8px] rounded-[4px] w-full" />
                                {errors.firstName !== ERROR_TYPES.NONE && (
                                    <p className="text-[12px] lg:text-[14px] text-red-600 mt-1">{errors.firstName}</p>
                                )}
                            </div>
                            <div className="flex flex-col w-full">
                                <label className="text-[14px] lg:text-base font-medium mb-1">Last Name<span className="text-red-600">*</span></label>
                                <input onBlur={handleBlur} value={form.lastName} onChange={handleChange} id="lastName" name="lastName" type="text" placeholder="Last Name" className="border-[1px] p-[10px] lg:p-[8px] rounded-[4px] w-full" />
                                {errors.lastName !== ERROR_TYPES.NONE && (
                                    <p className="text-[12px] lg:text-[14px] text-red-600 mt-1">{errors.lastName}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col w-full">
                            <label className="text-[14px] lg:text-base font-medium mb-1">Email<span className="text-red-600">*</span></label>
                            <input onBlur={handleBlur} value={form.email} onChange={handleChange} id="email" name="email" type="email" placeholder="Email" className="border-[1px] p-[10px] lg:p-[8px] rounded-[4px] w-full" />
                            {errors.email !== ERROR_TYPES.NONE && (
                                <p className="text-[12px] lg:text-[14px] text-red-600 mt-1">{errors.email}</p>
                            )}
                        </div>

                        <div className="flex flex-col w-full">
                            <label className="text-[14px] lg:text-base font-medium mb-1">Phone Number<span className="text-red-600">*</span></label>
                            <input onBlur={handleBlur} value={form.phoneNumber} onChange={handleChange} id="phoneNumber" name="phoneNumber" type="tel" placeholder="Phone Number" className="border-[1px] p-[10px] lg:p-[8px] rounded-[4px] w-full" />
                            {errors.phoneNumber !== ERROR_TYPES.NONE && (
                                <p className="text-[12px] lg:text-[14px] text-red-600 mt-1">{errors.phoneNumber}</p>
                            )}
                        </div>

                        <div className="flex flex-col w-full">
                            <label className="text-[14px] lg:text-base font-medium mb-1">Message<span className="text-red-600">*</span></label>
                            <textarea onBlur={handleBlur} value={form.message} onChange={handleChange} id="message" name="message" placeholder="Message" className="border-[1px] p-[10px] lg:p-[8px] rounded-[4px] min-h-[150px] lg:min-h-[250px] max-h-[500px] w-full"></textarea>
                            {errors.message !== ERROR_TYPES.NONE && (
                                <p className="text-[12px] lg:text-[14px] text-red-600 mt-1">{errors.message}</p>
                            )}
                        </div>

                        {/* Honeypot field for spam prevention */}
                        <div className="hidden">
                            <label>Honeypot</label>
                            <textarea value={form.honeyPot} onChange={handleChange} id="honeypot" name="honeyPot" placeholder="Honeypot" className="border-[1px] p-[4px] rounded-[4px]"></textarea>
                        </div>

                        <div className="flex w-full mt-2">
                            <button disabled={isSubmitting} className="bg-black text-white py-3 lg:py-[8px] px-6 lg:px-[24px] rounded-[8px] disabled:bg-gray-400 w-full sm:w-auto font-medium transition-colors" type="submit">
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
                
                
                <Toast
                    message={toast.message} 
                    type={toast.type} 
                    onClose={() => setToast({ message: '', type: '' })} 
                /> 
               
            </div>
        </div>
    );
}

export default ContactUs;